import { useCallback, useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { PaginaCrud, type CampoFormulario } from '../../components/layout/PaginaCrud';
import type { FilaInventario } from '../../../data/repositories/RepositorioInventario';
import { aNumero, aTexto } from '../../../data/mappers/conversiones';
import { LogoDeCliente } from './LogoDeCliente';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface Cliente {
  id: number;
  nombre: string;
  descripcion: string | null;
  estado: string;
}

const CAMPOS: CampoFormulario[] = [
  {
    clave: 'nombre',
    etiqueta: 'Nombre',
    tipo: 'texto',
    requerido: true,
    placeholder: 'Nombre de la empresa',
  },
  {
    clave: 'descripcion',
    etiqueta: 'Descripción',
    tipo: 'texto',
    placeholder: 'Opcional',
    ayuda: 'Referencia interna: sector, ubicación, contacto…',
  },
  {
    clave: 'estado',
    etiqueta: 'Estado',
    tipo: 'select',
    requerido: true,
    ayuda: 'Un cliente inactivo deja de recibir avisos',
    opciones: [
      { valor: 'activo', etiqueta: 'Activo' },
      { valor: 'inactivo', etiqueta: 'Inactivo' },
      { valor: 'suspendido', etiqueta: 'Suspendido' },
    ],
  },
];

export function Clientes() {
  const [logoDe, setLogoDe] = useState<Cliente | null>(null);

  const mapear = useCallback(
    (fila: FilaInventario): Cliente => ({
      id: aNumero(fila.id) ?? 0,
      nombre: aTexto(fila.nombre) ?? 'Sin nombre',
      descripcion: aTexto(fila.descripcion),
      estado: aTexto(fila.estado) ?? 'activo',
    }),
    [],
  );

  return (
    <>
      <PaginaCrud<Cliente>
        entidad="clientes"
        titulo="Clientes"
        descripcion="Empresas cuyas instalaciones se monitorean."
        textoVacio="Registra el primer cliente para poder darle áreas, nodos y sensores."
        mapear={mapear}
        claveFila={(c) => c.id}
        textoBusqueda={(c) => `${c.nombre} ${c.descripcion ?? ''}`}
        idDeFila={(fila) => aNumero(fila.id) ?? 0}
        campos={CAMPOS}
        aFormulario={(fila) => ({
          nombre: aTexto(fila.nombre) ?? '',
          descripcion: aTexto(fila.descripcion) ?? '',
          estado: aTexto(fila.estado) ?? 'activo',
        })}
        aRegistro={(v) => ({
          nombre: String(v.nombre ?? '').trim(),
          descripcion: String(v.descripcion ?? '').trim() || null,
          estado: v.estado,
        })}
        textoBorrado={(c) =>
          `¿Eliminar el cliente «${c.nombre}»?\n\n` +
          'Se perderá también todo lo que cuelga de él: áreas, nodos, sensores y su historial.'
        }
        columnas={[
          {
            clave: 'nombre',
            encabezado: 'Cliente',
            movil: 'titulo',
            render: (c) => <span className={estilos.nombre}>{c.nombre}</span>,
          },
          {
            clave: 'descripcion',
            encabezado: 'Descripción',
            movil: 'subtitulo',
            render: (c) => <span className={estilos.secundario}>{c.descripcion ?? '—'}</span>,
          },
          {
            clave: 'estado',
            encabezado: 'Estado',
            render: (c) => (
              <Badge
                color={
                  c.estado === 'activo'
                    ? 'var(--status-success)'
                    : c.estado === 'suspendido'
                      ? 'var(--status-warning)'
                      : 'var(--text-3)'
                }
              >
                {c.estado}
              </Badge>
            ),
          },
        ]}
        accionesExtra={(c) => (
          <Button
            tamano="sm"
            variante="secundario"
            icono="imagen"
            title="Logotipo del cliente"
            onClick={() => setLogoDe(c)}
          />
        )}
      />

      <LogoDeCliente
        abierto={logoDe !== null}
        clienteId={logoDe?.id ?? null}
        clienteNombre={logoDe?.nombre ?? ''}
        onCerrar={() => setLogoDe(null)}
      />
    </>
  );
}
